import { MapRounded } from '@mui/icons-material';
import { Box, Button, Paper, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { EventMapItem } from '../../../../types/Event';
import { MapboxTrip } from '../../../../types/Mapbox';
import { buildGoogleMapsDirectionsUrl } from '../../../../utility/map';
import EventMapMarker from '../EventMapMarker';
import EventMapTile from '../EventMapTile';
import { appZIndex } from '../../../../styles/zIndex';

interface Props {
  trip: MapboxTrip;
  orderedMapEvents: EventMapItem[];
}

export default function EventOptimizedRouteInfo({ trip, orderedMapEvents }: Props) {
  // Convert route summary values into user-friendly units
  const distanceKm = (trip.distance / 1000).toFixed(1);
  const durationMin = Math.round(trip.duration / 60);

  const googleMapsUrl = buildGoogleMapsDirectionsUrl(orderedMapEvents);

  return (
    <Paper
      elevation={8}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        position: 'absolute', // Sidebar displayed on top-left of the map
        top: 16,
        left: 16,
        zIndex: appZIndex.floating,
        width: 420,
        maxHeight: '50vh', // Keep the sidebar visible while allowing long routes to scroll
        overflowY: 'auto',
        borderRadius: 4,
        p: 2,
        bgcolor: 'background.paper',
        boxShadow: '0 10px 30px rgba(0,0,0,.15)',
        // Slightly nicer scrollbar appearance
        '&::-webkit-scrollbar': {
          width: 8,
        },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 10,
          bgcolor: 'divider',
        },
      }}
    >
      {/* Route summary header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" fontWeight={700}>
          Optimized Route
        </Typography>

        <Button size="small" variant="outlined" startIcon={<MapRounded />} onClick={() => window.open(googleMapsUrl, '_blank', 'noopener,noreferrer')}>
          Open in Google Maps
        </Button>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {/* Route statistics */}
        <Typography variant="body2" color="text.secondary">
          Stops: {orderedMapEvents.length}
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Distance: {distanceKm} km
        </Typography>

        <Typography variant="body2" color="text.secondary">
          Duration: {durationMin} mins
        </Typography>
      </Box>

      {/* Render events exactly in optimized order */}
      {_.map(orderedMapEvents, (mapEvent, index) => (
        <Box
          key={mapEvent.uuid}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            '&:last-child': {
              borderBottom: 'none',
            },
          }}
        >
          {/* Route stop marker with order number */}
          <Box
            sx={{
              width: 32,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            {/* <EventMapMarker optimized order={index + 1} size={32} color={mapEvent.location_color} /> */}
            <EventMapMarker optimized order={index + 1} />
          </Box>

          {/* Event information */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              '& > *': {
                boxShadow: 'none',
                border: 'none',
                p: 0,
              },
            }}
          >
            <EventMapTile mapEvent={mapEvent} />
          </Box>
        </Box>
      ))}
    </Paper>
  );
}
